import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import CountdownTimer from "@/components/countdown-timer";
import { 
  Trophy, 
  ArrowLeft, 
  CheckCircle, 
  Clock,
  Gift,
  Target,
  AlertCircle,
  BookOpen
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";

const answerSchema = z.object({
  answers: z.record(z.string().min(1, "Answer is required"))
});

type AnswerFormData = z.infer<typeof answerSchema>;

export default function CompetitionContinue() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});
  const [completedAnswers, setCompletedAnswers] = useState<Record<string, string>>({});

  const competitionId = parseInt(id || "0");
  const isAuthenticated = !!user;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to continue this competition",
        variant: "destructive",
      });
      setLocation("/auth");
      return;
    }
  }, [isAuthenticated, loading, toast, setLocation]);

  const form = useForm<AnswerFormData>({
    resolver: zodResolver(answerSchema),
    defaultValues: {
      answers: {}
    }
  });

  // Fetch competition details
  const { data: competition, isLoading: competitionLoading } = useQuery({
    queryKey: ["/api/competitions", competitionId],
    enabled: isAuthenticated && competitionId > 0,
  });

  // Fetch questions for this competition
  const { data: questions, isLoading: questionsLoading } = useQuery({
    queryKey: ["/api/competitions", competitionId, "questions"],
    enabled: isAuthenticated && competitionId > 0,
  });

  // Fetch user's existing answers
  const { data: existingAnswers, isLoading: answersLoading } = useQuery({
    queryKey: ["/api/user/answers", competitionId],
    enabled: isAuthenticated && competitionId > 0,
  });

  // Process existing answers to separate completed and remaining
  useEffect(() => {
    if (existingAnswers && questions) {
      const answered = existingAnswers.reduce((acc: Record<string, string>, answer: any) => {
        acc[answer.questionId] = answer.answer;
        return acc;
      }, {});
      setCompletedAnswers(answered);

      // Find the first unanswered question
      const firstUnanswered = questions.findIndex((q: any) => !answered[q.id]);
      if (firstUnanswered !== -1) {
        setCurrentQuestionIndex(firstUnanswered);
      }
    }
  }, [existingAnswers, questions]);

  // Submit answers mutation
  const submitAnswersMutation = useMutation({
    mutationFn: async (answers: Record<string, string>) => {
      const results = [];
      for (const [questionId, answer] of Object.entries(answers)) {
        const result = await apiRequest("POST", `/api/competitions/${competitionId}/answer`, {
          questionId: parseInt(questionId),
          answer: answer
        });
        results.push(result);
      }
      return results;
    },
    onSuccess: () => {
      toast({
        title: "Answers submitted!",
        description: "Your answers have been recorded and entries added.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/competitions", competitionId] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/answers", competitionId] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });
      setLocation(`/competition/${competitionId}`);
    },
    onError: (error: any) => {
      toast({
        title: "Error submitting answers",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = (data: AnswerFormData) => {
    const newAnswers = data.answers;
    const hasNewAnswers = Object.keys(newAnswers).some(key => newAnswers[key]?.trim());
    
    if (!hasNewAnswers) {
      toast({
        title: "No new answers",
        description: "Please answer at least one question before submitting",
        variant: "destructive",
      });
      return;
    }

    submitAnswersMutation.mutate(newAnswers);
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Update form
    const currentAnswers = form.getValues("answers");
    form.setValue("answers", {
      ...currentAnswers,
      [questionId]: answer
    });
  };

  const getRemainingQuestions = () => {
    if (!questions) return [];
    return questions.filter((q: any) => !completedAnswers[q.id]);
  };

  const getCompletedQuestions = () => {
    if (!questions) return [];
    return questions.filter((q: any) => completedAnswers[q.id]);
  };

  const remainingQuestions = getRemainingQuestions();
  const completedQuestions = getCompletedQuestions();
  const totalQuestions = questions?.length || 0;
  const completedCount = completedQuestions.length;
  const progressPercentage = totalQuestions > 0 ? (completedCount / totalQuestions) * 100 : 0;

  if (loading || competitionLoading || questionsLoading || answersLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading competition...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Competition Not Found</h1>
            <p className="text-gray-600 mb-4">The competition you're looking for doesn't exist.</p>
            <Button onClick={() => setLocation("/competitions")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Competitions
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Header */}
      <section className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => setLocation("/user")}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="text-right">
              <CountdownTimer endDate={competition.endDate} endTime={competition.endTime} />
            </div>
          </div>
        </div>
      </section>

      {/* Competition Info */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold">{competition.title}</CardTitle>
                  <p className="text-gray-600 mt-2">{competition.description}</p>
                </div>
                <div className="text-right">
                  <Badge variant="secondary" className="mb-2">
                    {competition.category}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-600">
                    <Gift className="w-4 h-4 mr-1" />
                    <span>{competition.prize}</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>{completedCount}/{totalQuestions} questions completed</span>
                  </div>
                  <Progress value={progressPercentage} className="h-2" />
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-600">{completedCount}</div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Target className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-600">{remainingQuestions.length}</div>
                    <div className="text-sm text-gray-600">Remaining</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <BookOpen className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-purple-600">{totalQuestions}</div>
                    <div className="text-sm text-gray-600">Total Questions</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              {/* Completed Questions (Greyed Out) */}
              {completedQuestions.length > 0 && (
                <Card className="border-green-200 bg-green-50/50">
                  <CardHeader>
                    <CardTitle className="text-lg text-green-800 flex items-center">
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Completed Questions ({completedQuestions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {completedQuestions.map((question: any, index: number) => (
                      <div key={question.id} className="p-4 bg-white/50 rounded-lg border border-green-200">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                              ✓
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-green-800 mb-2">{question.question}</h3>
                            <div className="text-sm text-green-700 bg-green-100 p-2 rounded">
                              <strong>Your answer:</strong> {completedAnswers[question.id]}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Remaining Questions */}
              {remainingQuestions.length > 0 && (
                <Card className="border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-blue-800 flex items-center">
                      <Target className="mr-2 h-5 w-5" />
                      Questions to Complete ({remainingQuestions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {remainingQuestions.map((question: any, index: number) => (
                      <div key={question.id} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                              {completedQuestions.length + index + 1}
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-3">{question.question}</h3>
                            
                            <FormField
                              control={form.control}
                              name={`answers.${question.id}`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    {question.type === 'multiple_choice' ? (
                                      <RadioGroup
                                        value={field.value}
                                        onValueChange={(value) => {
                                          field.onChange(value);
                                          handleAnswerChange(question.id.toString(), value);
                                        }}
                                      >
                                        {question.options?.map((option: string, optionIndex: number) => (
                                          <div key={optionIndex} className="flex items-center space-x-2">
                                            <RadioGroupItem value={option} id={`${question.id}-${optionIndex}`} />
                                            <Label htmlFor={`${question.id}-${optionIndex}`}>{option}</Label>
                                          </div>
                                        ))}
                                      </RadioGroup>
                                    ) : (
                                      <Textarea
                                        placeholder="Enter your answer..."
                                        value={field.value || ''}
                                        onChange={(e) => {
                                          field.onChange(e.target.value);
                                          handleAnswerChange(question.id.toString(), e.target.value);
                                        }}
                                        className="min-h-[100px]"
                                      />
                                    )}
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Submit Button */}
              {remainingQuestions.length > 0 && (
                <div className="flex justify-center">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitAnswersMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                  >
                    {submitAnswersMutation.isPending ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Trophy className="mr-2 h-5 w-5" />
                        Submit Answers & Earn Entries
                      </div>
                    )}
                  </Button>
                </div>
              )}

              {/* All Questions Completed */}
              {remainingQuestions.length === 0 && completedQuestions.length > 0 && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="text-center py-8">
                    <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-green-800 mb-2">All Questions Completed!</h3>
                    <p className="text-green-700 mb-4">
                      You've answered all {totalQuestions} questions in this competition.
                    </p>
                    <Button
                      onClick={() => setLocation(`/competition/${competitionId}`)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Trophy className="mr-2 h-4 w-4" />
                      Back to Competition
                    </Button>
                  </CardContent>
                </Card>
              )}
            </form>
          </Form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}